import { FC, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import { step7Schema, type Step7Data as FormData } from '@/validations';

const pitchStrategies = [
  { value: 'Consultative', label: 'Consultative', description: 'Focus on understanding and solving customer problems' },
  { value: 'ROI-Focused', label: 'ROI-Focused', description: 'Emphasize measurable returns and cost savings' },
  { value: 'Authority / Expert', label: 'Authority / Expert', description: 'Position as thought leader and industry expert' },
  { value: 'Challenger', label: 'Challenger', description: 'Challenge customer assumptions and offer new perspectives' },
  { value: 'Relationship', label: 'Relationship', description: 'Build trust and long-term partnerships' },
  { value: 'Direct / Action', label: 'Direct / Action', description: 'Clear, action-oriented approach to close deals' },
];

interface Step6PitchStrategyProps {
  initialData?: { pitchStrategy?: string };
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step6PitchStrategy: FC<Step6PitchStrategyProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(step7Schema),
    defaultValues: {
      strategy: initialData?.pitchStrategy as any,
    },
  });

  // Reset form when initialData changes, but only if it's actually different
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    if (skipReset) return;
    if (prevInitialDataRef.current?.pitchStrategy === initialData?.pitchStrategy && prevInitialDataRef.current) return;
    prevInitialDataRef.current = initialData;
    reset({
      strategy: initialData?.pitchStrategy as any,
    });
  }, [initialData, reset, skipReset]);

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Pitch Strategy</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Select your preferred sales pitch approach
          </p>
        </div>
      )}

      {/* Strategy Options */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700">
          Strategy <span className="text-red-500">*</span>
        </label>
        <Controller
          name="strategy"
          control={control}
          render={({ field }) => (
            <div className="space-y-2 mt-3">
              {pitchStrategies.map((strategy) => (
                <label
                  key={strategy.value}
                  className={`flex items-start px-3 py-2.5 border rounded-lg cursor-pointer transition-all ${field.value === strategy.value
                    ? 'border-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                  <input
                    type="radio"
                    value={strategy.value}
                    checked={field.value === strategy.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="mt-0.5 w-4 h-4 text-slate-900 border-slate-300 focus:ring-slate-900/10"
                  />
                  <div className="ml-2.5">
                    <div className="text-sm font-medium text-slate-900">{strategy.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{strategy.description}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
        />
        {errors.strategy && (
          <p className="text-xs text-red-600">{errors.strategy.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
